/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { mergeRegister } from "@lexical/utils";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { AbLocalToolbarGroup } from "abreact/incubator/ui/toolbar/AbLocalToolbar";
import { AbMarkdownLinkDialog } from "abreact/input/markdown/AbMarkdownLinkDialog";
import { createToolbar, replaceLinkText, useExtraActions } from "abreact/input/markdown/plugins/FloatingLinkEditorPlugin";
import { CLICK_COMMAND, COMMAND_PRIORITY_LOW, SELECTION_CHANGE_COMMAND } from "lexical";
import React from "react";
export const AbLinkComponent = ({
  nodeKey,
  linkText,
  linkUrl,
  children
}) => {
  const abLinkNodeComponentRef = React.useRef(null);
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [editor] = useLexicalComposerContext();
  const [showLinkDialog, setShowLinkDialog] = React.useState(false);
  const [popupElem, setPopupElem] = React.useState(undefined);
  const onClick = React.useCallback(payload => {
    if (!editor.isEditable()) {
      setPopupElem(undefined);
      return true;
    }
    const event = payload;
    let walker = event.target;
    let foundLink = false;
    // walk up the dom looking for the current abLinkNodeRef.
    while (walker !== null) {
      if (walker === abLinkNodeComponentRef.current) {
        foundLink = true;
        break;
      }
      // eslint-disable-next-line abinitio/no-instanceof
      if (walker instanceof Element) walker = walker.parentElement;else walker = null;
    }
    if (foundLink) {
      setPopupElem(abLinkNodeComponentRef.current || undefined);
      if (event.shiftKey) {
        setSelected(!isSelected);
      } else {
        clearSelection();
        setSelected(true);
      }
      // Tell the other listeners that we've selected the ablink.
      editor.dispatchCommand(SELECTION_CHANGE_COMMAND, undefined);
      return true;
    }
    return false; // click was not on an abLink, so let it propagate
  }, [isSelected, setSelected, clearSelection, editor]);
  React.useEffect(() => {
    const unregister = mergeRegister(editor.registerCommand(CLICK_COMMAND, onClick, COMMAND_PRIORITY_LOW));
    return () => {
      unregister();
    };
  }, [editor, onClick, setSelected]);
  const extraActions = useExtraActions(linkUrl);
  let extraActionsToolbarGroup = extraActions.length > 0 ? /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, extraActions) : null;
  return /*#__PURE__*/React.createElement("div", {
    ref: abLinkNodeComponentRef
  }, children, isSelected && popupElem !== undefined && /*#__PURE__*/React.createElement(AbPopup, {
    className: "ab-markdown-popup-toolbar",
    origin: popupElem,
    positioner: PopupUtils.VERTICAL_FIXED_SIZE_POSITIONER,
    overlay: false,
    flushContent: true,
    onClose: () => {
      setPopupElem(undefined);
    },
    onBackgroundCaptureMouseDown: () => {
      setPopupElem(undefined);
    }
  }, createToolbar(setShowLinkDialog, editor, extraActionsToolbarGroup), showLinkDialog && /*#__PURE__*/React.createElement(AbMarkdownLinkDialog, {
    text: linkText,
    url: linkUrl,
    buttonEl: null,
    onOk: (text, url) => {
      replaceLinkText(editor, text, url);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      return Promise.resolve(true);
    },
    onClose: () => {
      setShowLinkDialog(false); // turn off the link dialog
      setPopupElem(undefined); // turn off the toolbar
    }
  })));
};
//# sourceMappingURL=AbLinkComponent.js.map
